// CommandDock — Claude指示の汎用入口 (blueprint v2 Phase 1, ux_architect §3.1)
//   画面右下固定「Claudeに指示」ボタン(z-index 80) → ⌘K/Ctrl+K or クリックで
//   右ドロワー(480px)展開: 自由記述textarea + 文脈自動添付(location.hash / document.title を
//   payload.context に構造化) + 送信 → createJob('user_message') → JobChip表示。
//   会話履歴 = fetchJobs({type:'user_message', limit:10}) のスレッド表示。
//   スタイルは JournalReviewPage パレット踏襲のインライン style (vibes新規使用禁止)。
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdAutoAwesome, MdClose, MdSend } from 'react-icons/md';
import { Job, createJob, fetchJobs } from '../api/client';
import {
  JOBS_C as C,
  JOBS_FONT as FONT,
  JOBS_NUM as NUM,
  JobLiveLine,
  JobStatusBadge,
  fmtElapsed,
  isActiveJobStatus,
  jobErrorLine,
  jobResultLine,
} from './JobTray';

// ---------------------------------------------------------------------------
// payload helpers
// ---------------------------------------------------------------------------
interface PageContext {
  page_hash: string;
  page_title: string;
  url: string;
  captured_at: string;
}

const captureContext = (): PageContext => ({
  page_hash: window.location.hash || '#invoice',
  page_title: document.title || '',
  url: window.location.href,
  captured_at: new Date().toISOString(),
});

const jobText = (j: Job): string => {
  try {
    const p = JSON.parse(j.payload_json || '{}');
    return String(p.text || p.message || '').slice(0, 500);
  } catch {
    return '';
  }
};

const jobContextHash = (j: Job): string | null => {
  try {
    const p = JSON.parse(j.payload_json || '{}');
    return p.context?.page_hash ? String(p.context.page_hash) : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// styles
// ---------------------------------------------------------------------------
const S = {
  dockBtn: {
    position: 'fixed', right: 16, bottom: 16, zIndex: 80,
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '10px 16px', borderRadius: 22, cursor: 'pointer',
    border: `1px solid ${C.navy}`, background: C.navy, color: '#fff',
    font: `800 13px ${FONT}`, boxShadow: '0 6px 18px rgba(33,52,74,.28)',
  } as React.CSSProperties,
  kbdHint: {
    padding: '1px 6px', borderRadius: 3, border: '1px solid rgba(255,255,255,.35)',
    background: 'rgba(255,255,255,.12)', color: '#e5eef8',
    fontFamily: NUM, fontSize: 10, fontWeight: 800,
  } as React.CSSProperties,
  drawer: {
    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 90,
    width: 480, maxWidth: '100vw',
    display: 'flex', flexDirection: 'column',
    background: '#fff', borderLeft: `1px solid ${C.lineDark}`,
    boxShadow: '-12px 0 30px rgba(33,52,74,.22)',
    fontFamily: FONT, color: C.ink, fontSize: 13,
  } as React.CSSProperties,
  head: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    padding: '12px 16px', background: C.navy, color: '#fff',
  } as React.CSSProperties,
  headTitle: { margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: '.03em', display: 'flex', alignItems: 'center', gap: 7 } as React.CSSProperties,
  closeBtn: {
    display: 'grid', placeItems: 'center', width: 28, height: 28, borderRadius: 4,
    border: '1px solid rgba(255,255,255,.35)', background: 'rgba(255,255,255,.1)',
    color: '#e5eef8', cursor: 'pointer',
  } as React.CSSProperties,
  form: { padding: '12px 16px', borderBottom: `1px solid ${C.line}`, background: '#fcfdfe' } as React.CSSProperties,
  textarea: {
    width: '100%', minHeight: 88, padding: 9, boxSizing: 'border-box',
    border: `1px solid ${C.lineDark}`, borderRadius: 5, resize: 'vertical',
    font: `400 13px ${FONT}`, color: C.ink, background: '#fff',
  } as React.CSSProperties,
  ctxRow: {
    display: 'flex', alignItems: 'center', gap: 7, marginTop: 8,
    color: C.sub, fontSize: 11.5, fontWeight: 700, flexWrap: 'wrap',
  } as React.CSSProperties,
  ctxChip: {
    display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 300,
    padding: '3px 8px', borderRadius: 10, border: `1px solid ${C.blueLine}`,
    background: C.blueTint, color: '#234e79', fontSize: 11, fontWeight: 700,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  } as React.CSSProperties,
  typeNote: { marginTop: 6, color: C.sub, fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em' } as React.CSSProperties,
  sendRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 } as React.CSSProperties,
  sendBtn: (disabled: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 18px', borderRadius: 5,
    border: `1px solid ${disabled ? C.lineDark : C.navy}`,
    background: disabled ? '#f1f4f7' : C.navy, color: disabled ? '#93a3b3' : '#fff',
    font: `800 13px ${FONT}`, cursor: disabled ? 'default' : 'pointer',
  }),
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '3px 9px', borderRadius: 12, border: `1px solid ${C.lineDark}`,
    background: '#fff', fontSize: 11, fontWeight: 700, color: '#3c4c5e',
  } as React.CSSProperties,
  chipId: { fontFamily: NUM, fontSize: 10, fontWeight: 800, color: '#476b8e' } as React.CSSProperties,
  sendErr: { marginTop: 7, color: C.danger, fontSize: 11.5, fontWeight: 700 } as React.CSSProperties,
  histHead: {
    padding: '9px 16px 5px', color: C.sub, fontSize: 10, letterSpacing: '.08em', fontWeight: 800,
  } as React.CSSProperties,
  hist: { flex: 1, overflowY: 'auto', padding: '0 16px 16px', background: '#f7f9fb' } as React.CSSProperties,
  msg: {
    padding: '9px 11px', marginTop: 8, borderRadius: 6,
    border: `1px solid ${C.line}`, background: '#fff',
  } as React.CSSProperties,
  msgTop: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 } as React.CSSProperties,
  msgWho: { flex: '0 0 auto', color: '#22364b', fontSize: 11, fontWeight: 800 } as React.CSSProperties,
  msgTime: {
    marginLeft: 'auto', flex: '0 0 auto', color: C.sub,
    fontFamily: NUM, fontSize: 10.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
  msgText: { margin: '6px 0 0', fontSize: 12.5, fontWeight: 600, color: '#2c3e52', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } as React.CSSProperties,
  msgCtx: { marginTop: 5, color: C.sub, fontSize: 10.5, fontWeight: 700 } as React.CSSProperties,
  msgResult: (ok: boolean): React.CSSProperties => ({
    margin: '7px 0 0', padding: '6px 9px', borderRadius: 4, fontSize: 11.5, fontWeight: 700,
    color: ok ? C.good : C.danger, background: ok ? C.goodBg : C.dangerBg,
    border: `1px solid ${ok ? '#9fdbc7' : '#e6a49e'}`,
    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  }),
  histEmpty: { padding: '18px 8px', textAlign: 'center', color: C.sub, fontSize: 12, fontWeight: 600 } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------
export const CommandDock: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [attachCtx, setAttachCtx] = useState(true);
  const [ctx, setCtx] = useState<PageContext | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [lastJob, setLastJob] = useState<{ id: string; status: string } | null>(null);
  const [thread, setThread] = useState<Job[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({}); // status別実数 (queued前方件数の概算用)
  const [now, setNow] = useState<number>(Date.now());
  const aliveRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // 会話履歴 (user_message ジョブ直近10件)。drawer open中のみpoll: 実行中あり10秒/無し30秒
  const loadThread = useCallback(async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    let items: Job[] = [];
    try {
      const r = await fetchJobs({ type: 'user_message', limit: 10 });
      items = (r.jobs || []).slice().sort((a, b) => a.created_at - b.created_at); // 古→新 (チャット順)
      if (!aliveRef.current) return;
      if (r.counts) setCounts(r.counts); // LIMIT非依存のstatus別実数
      setThread(items);
      setNow(Date.now());
      setLastJob((prev) => {
        if (!prev) return prev;
        const hit = items.find((j) => j.id === prev.id);
        return hit ? { id: hit.id, status: hit.status } : prev;
      });
    } catch {
      /* 履歴取得失敗は無音でリトライ (送信systemには影響しない) */
    }
    if (!aliveRef.current) return;
    const hasActive = items.some((j) => isActiveJobStatus(j.status));
    timerRef.current = setTimeout(loadThread, hasActive ? 10_000 : 30_000);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    if (open) {
      setCtx(captureContext());
      loadThread();
      // 開いた直後にtextareaへfocus
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, loadThread]);

  // 他画面からのプレフィル受信 (例: InvoiceRunsPage の [Claudeに指示])。
  // window.dispatchEvent(new CustomEvent('freeeman:dock-prefill', {detail:{text}})) で
  // drawer を開き textarea に文脈をセットする。送信は user が行う (既存 submit をそのまま利用)。
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const t = String((e as CustomEvent).detail?.text ?? '');
      setOpen(true);
      if (t) setText(t);
      // open effect の focus(50ms) 後にカーソルを末尾へ
      setTimeout(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          const n = el.value.length;
          el.setSelectionRange(n, n);
        }
      }, 80);
    };
    window.addEventListener('freeeman:dock-prefill', onPrefill);
    return () => window.removeEventListener('freeeman:dock-prefill', onPrefill);
  }, []);

  // ⌘K / Ctrl+K で開閉 + Esc で閉じる
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const submit = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const payload: Record<string, any> = { text: t, source: 'command_dock' };
      if (attachCtx) payload.context = captureContext();
      const r = await createJob('user_message', payload, { created_by: 'web_app_ui' });
      setLastJob({ id: r.id, status: r.status }); // JobChip (楽観表示: queued/awaiting_approval)
      setText('');
      loadThread();
    } catch (e: any) {
      setSendError(e?.message || '送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  const onTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const pageLabel = ctx ? `${ctx.page_title || 'FREEEMAN'} ${ctx.page_hash}` : '';

  // queued の前方件数概算: counts(claimed/running 実数) + スレッド内の先行 queued。counts 無しは概算不能(null)
  const queueAheadOf = (j: Job): number | null => {
    if (!Object.keys(counts).length) return null;
    const earlierQueued = thread.filter(
      (x) => x.id !== j.id && x.status === 'queued'
        && (x.created_at < j.created_at || (x.created_at === j.created_at && x.id < j.id)),
    ).length;
    return (counts['claimed'] || 0) + (counts['running'] || 0) + earlierQueued;
  };

  return (
    <>
      <button
        type="button"
        style={S.dockBtn}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Claudeに指示 (Ctrl+K / ⌘K)"
      >
        <MdAutoAwesome size={16} aria-hidden />
        <span>Claudeに指示</span>
        <span style={S.kbdHint}>⌘K</span>
      </button>
      {open && (
        <div style={S.drawer} role="dialog" aria-label="Claudeへの指示">
          <div style={S.head}>
            <h3 style={S.headTitle}><MdAutoAwesome size={17} aria-hidden />Claudeへの指示</h3>
            <button type="button" style={S.closeBtn} aria-label="閉じる" onClick={() => setOpen(false)}>
              <MdClose size={16} />
            </button>
          </div>
          <div style={S.form}>
            <textarea
              ref={textareaRef}
              style={S.textarea}
              value={text}
              placeholder="自由記述で指示を入力 (Ctrl+Enterで送信)&#10;例: JCBの摘要の分類ルールを見直して"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onTextKeyDown}
            />
            <div style={S.ctxRow}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={attachCtx}
                  onChange={(e) => setAttachCtx(e.target.checked)}
                />
                文脈を添付
              </label>
              {attachCtx && ctx && (
                <span style={S.ctxChip} title={ctx.url}>このページ: {pageLabel}</span>
              )}
            </div>
            <div style={S.typeNote}>種別: user_message(自動判定)</div>
            <div style={S.sendRow}>
              <button
                type="button"
                style={S.sendBtn(sending || !text.trim())}
                disabled={sending || !text.trim()}
                onClick={submit}
              >
                <MdSend size={14} aria-hidden />
                {sending ? '送信中…' : '送信'}
              </button>
              {lastJob && (
                <span style={S.chip}>
                  <span style={S.chipId}>{lastJob.id}</span>
                  <JobStatusBadge status={lastJob.status as any} />
                </span>
              )}
            </div>
            {sendError && <div style={S.sendErr}>{sendError}</div>}
          </div>
          <div style={S.histHead}>会話履歴(user_message 直近10件)</div>
          <div style={S.hist}>
            {thread.length === 0 && <div style={S.histEmpty}>まだ指示はありません</div>}
            {thread.map((j) => {
              const resultLine = jobResultLine(j);
              const errLine = jobErrorLine(j);
              const ctxHash = jobContextHash(j);
              return (
                <div key={j.id} style={S.msg}>
                  <div style={S.msgTop}>
                    <span style={S.msgWho}>あなた</span>
                    <JobStatusBadge status={j.status} />
                    <span style={S.msgTime}>{fmtElapsed(j.created_at, now)}</span>
                  </div>
                  {isActiveJobStatus(j.status) && (
                    <div style={{ marginTop: 6 }}>
                      <JobLiveLine
                        job={j}
                        nowMs={now}
                        queueAhead={j.status === 'queued' ? queueAheadOf(j) : null}
                      />
                    </div>
                  )}
                  <p style={S.msgText}>{jobText(j) || '(本文なし)'}</p>
                  {ctxHash && <div style={S.msgCtx}>文脈: {ctxHash}</div>}
                  {j.status === 'succeeded' && (
                    <div style={S.msgResult(true)}>完了: {resultLine || '処理が完了しました'}</div>
                  )}
                  {(j.status === 'failed' || j.status === 'dead_letter') && (
                    <div style={S.msgResult(false)}>エラー: {errLine || '失敗しました(詳細はジョブトレイ)'}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default CommandDock;
